import { useEffect, useState } from 'react';
import { ChoiceMessage, Coordinates, GameMode, LevelProgressState, RegionalMode } from '../types';
import { REGIONAL_MODES, MODE_LABELS } from '../utils/modes';
import { loadLocalWarningAcknowledged, saveLocalWarningAcknowledged } from '../utils/storage';
import { ExperienceProgress } from './ExperienceProgress';
import { PinLocationDialog } from './PinLocationDialog';

interface Props {
  onStart: (mode: GameMode, seedOverride?: string, options?: { userProvidedSeed?: boolean }) => void;
  onStartPin: (location: Coordinates) => void;
  onViewStats: () => void;
  onViewSettings: () => void;
  onRules: () => void;
  onAchievements: () => void;
  seedValue: string;
  onSeedChange: (value: string) => void;
  dailyLocked: boolean;
  statusMessage: ChoiceMessage | null;
  totalExp: number;
  levelProgress: LevelProgressState;
  regionalCityCounts: Record<RegionalMode, number>;
  hasSavedLocation: boolean;
  savedPinLocation: Coordinates | null;
}

export function LandingView({
  onStart,
  onStartPin,
  onViewStats,
  onViewSettings,
  onRules,
  onAchievements,
  seedValue,
  onSeedChange,
  dailyLocked,
  statusMessage,
  totalExp,
  levelProgress,
  regionalCityCounts,
  hasSavedLocation,
  savedPinLocation,
}: Props) {
  const [hoursUntilReset, setHoursUntilReset] = useState(() => {
    const now = new Date();
    const nextUtcMidnight = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate() + 1, 0, 0, 0);
    const diffMs = nextUtcMidnight - now.getTime();
    return Math.max(1, Math.ceil(diffMs / (1000 * 60 * 60)));
  });

  useEffect(() => {
    const timer = window.setInterval(() => {
      const now = new Date();
      const nextUtcMidnight = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate() + 1, 0, 0, 0);
      const diffMs = nextUtcMidnight - now.getTime();
      setHoursUntilReset(Math.max(1, Math.ceil(diffMs / (1000 * 60 * 60))));
    }, 60 * 1000);
    return () => window.clearInterval(timer);
  }, []);

  const randomLabel = seedValue.trim() ? 'Play seeded game' : 'Play random run';
  const trimmedSeed = seedValue.trim();
  const isSeededLaunch = Boolean(trimmedSeed);
  const [showLocalWarning, setShowLocalWarning] = useState(false);
  const [localWarningAcknowledged, setLocalWarningAcknowledged] = useState(() => loadLocalWarningAcknowledged());
  const [showPinDialog, setShowPinDialog] = useState(false);
  const top100Label = MODE_LABELS['top-100'];

  const handleLocalStart = () => {
    if (localWarningAcknowledged) {
      onStart('local');
      return;
    }
    setShowLocalWarning(true);
  };

  const handleConfirmLocal = () => {
    saveLocalWarningAcknowledged();
    setLocalWarningAcknowledged(true);
    setShowLocalWarning(false);
    onStart('local');
  };

  const handleStartPin = (location: Coordinates) => {
    setShowPinDialog(false);
    onStartPin(location);
  };
  return (
    <section>
      <div className="page-header">
        <div className="title-stack">
          <h1>City Pop</h1>
          <p className="helper-text">Pick which city is bigger.</p>
        </div>
        <div className="nav-row">
          <button className="btn ghost" onClick={onViewStats} aria-label="View stats">
            Stats
          </button>
          {/* <button className="btn ghost" onClick={onViewSettings} aria-label="View settings">
            Settings
          </button> */}
          <button className="btn ghost" onClick={onAchievements} aria-label="View achievements">
            Achievements
          </button>
          <button className="btn ghost" onClick={onRules} aria-label="Open rules">
            Rules
          </button>
        </div>
      </div>
      <div className="menu-grid">
        <article className="menu-card full-span level-card">
          <ExperienceProgress totalExp={totalExp} animateFrom={totalExp} />
        </article>
      </div>
      <div className="menu-grid two-up">
        <article className="menu-card">
          <div className="menu-card-header">
            <div>
              <p className="eyebrow">Today</p>
              <h2>Daily Challenge</h2>
              <p className="helper-text">This Challenge resets each UTC day. Next reset in {hoursUntilReset}h.</p>
            </div>
            <span className="pill">{hoursUntilReset}h</span>
          </div>
          <div className="card-footer">
            <button className="btn primary" onClick={() => onStart('daily')} disabled={dailyLocked}>
              {dailyLocked ? 'Already played' : 'Play daily challenge'}
            </button>
          </div>
        </article>
        <article className="menu-card">
          <div className="menu-card-header">
            <div>
              <p className="eyebrow">Endless</p>
              <h2>Free play</h2>
              <p className="helper-text">Spin up a random seed or set your own.</p>
            </div>
            <span className="pill alt">∞</span>
          </div>
          <div className="card-footer">
            <div className="seed-row">
              <input
                id="seed-input"
                className="text-input"
                value={seedValue}
                onChange={(event) => onSeedChange(event.target.value)}
                placeholder="Type a seed to replay a run"
                aria-label="Seed"
                data-testid="seed-input"
              />
              <button
                className="btn primary"
                onClick={() => onStart('random', isSeededLaunch ? trimmedSeed : undefined, { userProvidedSeed: isSeededLaunch })}
              >
                {randomLabel}
              </button>
            </div>
          </div>
        </article>
      </div>
      <h3 className="eyebrow">Special</h3>
      <div className="menu-grid">
        <article className="menu-card">
          <div className="menu-card-header">
            <div>
              <p className="eyebrow">Worldwide</p>
              <h2>{top100Label}</h2>
              <p className="helper-text">Only the 100 most populated cities are in rotation.</p>
            </div>
            <span className="pill alt">100</span>
          </div>
          <div className="card-footer">
            <button className="btn primary solid" onClick={() => onStart('top-100')}>
              Play {top100Label}
            </button>
          </div>
        </article>
        <article className="menu-card">
          <div className="menu-card-header">
            <div>
              <p className="eyebrow">Nearby</p>
              <h2>Local run</h2>
              <p className="helper-text">
                Uses your location to pull the closest 100 cities.
              </p>
            </div>
            <span className="pill neutral">📍</span>
          </div>
          <div className="card-footer">
            <p className="helper-text">
              {hasSavedLocation ? 'Saved location ready' : 'You will be asked for permission when starting.'}
            </p>
            <button className="btn primary solid" onClick={handleLocalStart}>
              Play local game
            </button>
          </div>
        </article>
        <article className="menu-card">
          <div className="menu-card-header">
            <div>
              <p className="eyebrow">Pin</p>
              <h2>Pin run</h2>
              <p className="helper-text">
                Drop a pin anywhere and we will pick the closest 100 cities for your run.
              </p>
            </div>
            <span className="pill alt">📌</span>
          </div>
          <div className="card-footer">
            <p className="helper-text">
              {savedPinLocation ? 'Saved pin ready to reuse.' : 'Choose a location to start your first pin run.'}
            </p>
            <button className="btn primary solid" onClick={() => setShowPinDialog(true)}>
              Play pin run
            </button>
          </div>
        </article>
      </div>
      <div className="menu-grid continent-grid">
        {REGIONAL_MODES.map((region) => {
          const cityCount = regionalCityCounts[region] ?? 0;
          const label = MODE_LABELS[region] ?? region;
          return (
            <article key={region} className="menu-card region-card">
              <div className="menu-card-header">
                <div>
                  <p className="eyebrow">Region</p>
                  <h2 className="continent-name">{label.replace(' run', '')}</h2>
                  <p className="helper-text">{cityCount} cities in rotation</p>
                </div>
              </div>
              <button className="btn primary solid" onClick={() => onStart(region)} disabled={cityCount < 2}>
                Play {label}
              </button>
            </article>
          );
        })}
      </div>
      {statusMessage ? <div className={`notice ${statusMessage.tone}`}>{statusMessage.message}</div> : null}
      {showLocalWarning ? (
        <div className="dialog-backdrop" role="presentation">
          <div className="dialog-card" role="dialog" aria-modal="true" aria-labelledby="local-warning-title">
            <p className="eyebrow">Special</p>
            <h2 id="local-warning-title">Local run uses your location</h2>
            <p className="helper-text">
              This mode uses your current location to build a pool from the 100 closest cities. Precise coordinates are
              never shown on screen, but nearby cities can still reveal your area.
            </p>
            <p className="helper-text">
              This is especially sensitive for streamers on Twitch, YouTube, or other platforms. You are about to be
              asked for location permission.
            </p>
            <div className="dialog-actions">
              <button className="btn ghost" onClick={() => setShowLocalWarning(false)}>
                Cancel
              </button>
              <button className="btn primary solid" onClick={handleConfirmLocal}>
                Continue
              </button>
            </div>
          </div>
        </div>
      ) : null}
      <PinLocationDialog
        isOpen={showPinDialog}
        initialLocation={savedPinLocation}
        onCancel={() => setShowPinDialog(false)}
        onConfirm={handleStartPin}
      />
    </section>
  );
}
